// Filename: NotebookApproveFormPage.tsx
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Container, Grid, TextField, Typography, MenuItem, IconButton, Divider,
    Breadcrumbs, Link, Modal, capitalize, Avatar, Tooltip, Chip, useTheme
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useNavigate, useParams } from "react-router-dom";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useAuth } from "../../../helpers/AuthProvider"; // Adjust path if needed
import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields"; // Adjust path if needed
import queryGraphql from "../../../helpers/ApiInterface"; // Adjust path if needed
import MiniBot from "../../MiniBot"; // Adjust path if needed
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed
import { Close } from '@mui/icons-material';

// --- Validation Schemas ---
const approvalValidationSchema = Yup.object().shape({
    jupyterLabVersion: Yup.string().required("This field is required"),
    machineType: Yup.string().required("This field is required"),
    network: Yup.string().required("This field is required").strict().lowercase(),
    subNetwork: Yup.string().required("This field is required").strict().lowercase(),
});

const rejectValidationSchema = Yup.object().shape({
    reasonForRejection: Yup.string()
        .required("This field is required")
        .matches(/^[a-zA-Z0-9-\s.,!?()']*$/, "Comments can only contain alphanumeric characters and basic punctuation.")
        .strict(),
});

// --- Data for Dropdowns ---
const jupyterLabVersionList = ["4.x", "3.x"];
const providerMachineTypes = {
    gcp: [
        "e2-standard-2", "e2-standard-4", "e2-standard-8", "e2-standard-16",
        "n2-standard-2", "n2-standard-4", "n2-standard-8", "n2-standard-16",
        "n1-standard-2", "n1-standard-4", "n1-standard-8", "n1-standard-16",
        "g2-standard-4", "g2-standard-8", // GPU Types
        "a2-highgpu-1g", "a2-highgpu-2g", // GPU Types
    ],
    aws: [
        "t3.medium", "t3.large", "t3.xlarge", "t3.2xlarge",
        "m5.large", "m5.xlarge", "m5.2xlarge", "m5.4xlarge",
        "c5.large", "c5.xlarge", "c5.2xlarge", "c5.4xlarge",
        "g4dn.xlarge", "g4dn.2xlarge", "g5.xlarge", // GPU Types
        "p3.2xlarge", "p4d.24xlarge", // GPU Types
    ],
    azure: [
        "Standard_DS2_v2", "Standard_DS3_v2", "Standard_DS4_v2",
        "Standard_F2s_v2", "Standard_F4s_v2", "Standard_F8s_v2",
        "Standard_D4as_v4", "Standard_D8as_v4", "Standard_D16as_v4",
        "Standard_NC4as_T4_v3", "Standard_NC6s_v3", "Standard_ND96asr_v4", // GPU Types
    ],
};


// --- Helper Component ---
const DetailItem: React.FC<{ label: string; value?: string | number | null; }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
        </Box>
    );
};


// --- Main Content Component ---
const NotebookApproveFormPageContent: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { user } = useAuth();
    const [notebook, setNotebook] = useState<any>(null);
    const [openRejectModal, setOpenRejectModal] = useState(false);
    const [openApproveModal, setOpenApproveModal] = useState(false);

    React.useEffect(() => {
        const fetchNotebookData = async (notebookId: string) => {
            try {
                const data = await queryGraphql(
                    `
                    query GetNotebook($getNotebookId: String!) {
                      getNotebook(id: $getNotebookId) {
                        id
                        name
                        cloudProvider
                        application {
                           id
                           applicationName
                        }
                        status
                        createdAt
                        updatedAt
                        reasonForRejection
                        solutionOwner {
                          name
                          googleProfilePicture
                        }
                        costLabelPairs {
                          cost
                          label
                        }
                        details {
                          notebooks {
                            region
                            zone
                            workbenchType
                            jupyterLabVersion
                            machineType
                            subNetwork
                            network
                          }
                        }
                      }
                    }
                    `,
                    { getNotebookId: notebookId }
                );
                if (data.getNotebook) {
                    setNotebook(data.getNotebook);
                }
            } catch (error) {
                console.log("Error fetching notebook details:", error);
            }
        };
        if (id) fetchNotebookData(id);
    }, [id]);
    console.log("notebook data", notebook);
    const getStatusInfo = (status: string | undefined) => {
        if (!status) return { text: 'Unknown', color: '#FDB02210', icon: '/assets/clock.svg' };
        const lowerStatus = status.toLowerCase();
        if (["active", "approved", "success"].includes(lowerStatus)) return { text: 'Active', color: '#12B76A10', icon: '/assets/tickcircle.svg' };
        if (["rejected", "failed"].includes(lowerStatus)) return { text: 'Rejected', color: '#F0443810', icon: '/assets/reject.svg' };
        return { text: 'Pending', color: '#FDB02210', icon: '/assets/clock.svg' };
    };
    const statusInfo = getStatusInfo(notebook?.status);
    
    // Determine the correct machine type list based on the notebook's cloud provider
    const machineTypeList = notebook?.cloudProvider ? (providerMachineTypes as any)[notebook.cloudProvider.toLowerCase()] || [] : [];

    return (
        <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
            <MiniBot />
            <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
                    <Link underline="hover" color="inherit" href="/aisolutions/requests/notebooks">
                        Requests
                    </Link>
                    <Typography color="text.primary">Request Details</Typography>
                </Breadcrumbs>
                <Box display="flex" alignItems="center" mt={1}>
                    <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/notebooks")}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography fontFamily="Gilroy-Bold" fontSize="18px">
                        Request Details
                    </Typography>
                </Box>
            </Box>
            <Grid container>
                <Grid item xs={0.5} />
                <Grid item xs={11}>
                    {notebook && (
                        <Formik
                            initialValues={{
                                jupyterLabVersion: '4.x',
                                machineType: machineTypeList[0] || '',
                                network: '',
                                subNetwork: '',
                            }}
                            validationSchema={approvalValidationSchema}
                            enableReinitialize={true} // Allows form to reinitialize when machineTypeList changes
                            onSubmit={async (values) => {
                                const approvalInput = {
                                    id: notebook.id,
                                    details:{
                                        notebooks:{
                                            jupyterLabVersion: values.jupyterLabVersion,
                                            machineType : values.machineType,
                                            subNetwork : values.subNetwork,
                                            network : values.network
                                        }
                                    },
                                };
                                try {
                                    const approvalData = await queryGraphql(
                                        `mutation ApproveSolutionNotebooks($input: ApproveSolutionInput) {
                                            approveSolutionNotebooks(input: $input) { id }
                                        }`,
                                        { input: {
                                            id: notebook.id,
                                    details:{
                                        notebooks:{
                                            jupyterLabVersion: values.jupyterLabVersion,
                                            machineType : values.machineType,
                                            subNetwork : values.subNetwork,
                                            network : values.network
                                        }
                                    },
                                        } }
                                    );
                                    if (approvalData?.approveSolutionNotebooks?.id ) {
                                        navigate("/aisolutions/requests/notebooks");
                                    }
                                } catch (err) {
                                    console.error("Approval failed:", err);
                                }
                            }}
                        >
                            {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                                <Form id="notebook-approve-form">
                                    <Box mx={4}>
                                        <Typography fontSize="16px" my={1}>
                                            Review the notebook request and fill the environment & networking details to approve.
                                        </Typography>
                                        <Box>
                                            <Box bgcolor={statusInfo.color} justifyContent="space-between" alignItems="center" display="flex" borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
                                                <Box>
                                                    <Typography fontSize="16px" fontFamily="Gilroy-Bold">{notebook.name}</Typography>
                                                    <Typography fontFamily="Gilroy-Regular" fontSize="12px">{`Requested By: ${notebook.solutionOwner?.name} | On: ${moment(Number(notebook.createdAt)).format("DD/MM/YYYY")}`}</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                                    <img src={statusInfo.icon} alt="Status" style={{ width: "20px", marginRight: "8px" }} />
                                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">{statusInfo.text}</Typography>
                                                </Box>
                                            </Box>
                                            <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={12} sm={3}><DetailItem label="Cloud Provider" value={notebook.cloudProvider} /></Grid>
                                                    <Grid item xs={12} sm={3}><DetailItem label="Application" value={notebook.application?.applicationName} /></Grid>
                                                    <Grid item xs={12} sm={3}><DetailItem label="Workbench Type" value={notebook.details.notebooks.workbenchType} /></Grid>
                                                    <Grid item xs={12} sm={3}><DetailItem label="Region" value={notebook.details.notebooks.region} /></Grid>
                                                    <Grid item xs={12} sm={3}><DetailItem label="Zone" value={notebook.details.notebooks.zone} /></Grid>
                                                </Grid>
                                            </Box>

                                            {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
                                                <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
                                                    <Typography fontSize="14px" fontFamily="Gilroy-Bold">Environment & Networking Details</Typography>
                                                    <Divider sx={{ marginY: 2 }} />
                                                    <Grid container spacing={2}>
                                                        <Grid item xs={12} sm={6}>
                                                            <CompulsoryFieldsLabel fieldName="JupyterLab version" />
                                                            <TextField select fullWidth name="jupyterLabVersion" value={values.jupyterLabVersion} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.jupyterLabVersion && Boolean(errors.jupyterLabVersion)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                                {jupyterLabVersionList.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                                                            </TextField>
                                                            <ErrorMessage name="jupyterLabVersion">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <CompulsoryFieldsLabel fieldName="Machine type" />
                                                            <TextField select fullWidth name="machineType" value={values.machineType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.machineType && Boolean(errors.machineType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
                                                                {machineTypeList.map((t: string) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                                                            </TextField>
                                                            <ErrorMessage name="machineType">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <CompulsoryFieldsLabel fieldName="Network" />
                                                            <TextField fullWidth name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/global/networks/vpc-name" error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <ErrorMessage name="network">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                                        </Grid>
                                                        <Grid item xs={12} sm={6}>
                                                            <CompulsoryFieldsLabel fieldName="Sub-network" />
                                                            <TextField fullWidth name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/regions/us-central1/subnetworks/subnet-name" error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
                                                            <ErrorMessage name="subNetwork">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            )}
                                            
                                            {notebook.status !== "pending" && (
                                                <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
                                                    {notebook.status === "active" && (
                                                        <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
                                                                            
                                                                            <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
                                                                                Environment and Networking
                                                                            </Typography>
                                                                            <Grid container spacing={3}>
                                                                                <Grid item xs={6} sm={3}>
                                                                                    <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
                                                                                    <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.jupyterLabVersion}</Typography>
                                                                                </Grid>
                                                                                <Grid item xs={6} sm={3}>
                                                                                    <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
                                                                                    <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.machineType}</Typography>
                                                                                </Grid>
                                                                                <Grid item xs={6} sm={3}>
                                                                                    <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
                                                                                    <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.network}</Typography>
                                                                                </Grid>
                                                                                <Grid item xs={6} sm={3}>
                                                                                    <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
                                                                                    <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.subNetwork}</Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Box>
                                                    )}
                                                    {notebook.status === "rejected" && <DetailItem label="Reason for Rejection" value={notebook.reasonForRejection} />}
                                                    
                                                </Box>
                                            )}

                                            {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
                                                <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
                                                    <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
                                                    <Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
                                                            variant="contained" 
                                                            size="large"  disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
                                                </Box>
                                            )}
                                        </Box>

                                        <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
                                            <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 600}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
                                                <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Notebook</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close /></IconButton></Box>
                                                <Divider />
                                                <Typography p={3}>Are you sure you want to approve this Notebook Request?</Typography>
                                                <Divider />
                                                <Box sx={{ p: 2, textAlign: "right" }}>
                                                    <Button type="submit" form="notebook-approve-form" sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
                                                            variant="contained" 
                                                            size="large">Approve</Button>
                                                </Box>
                                            </Box>
                                        </Modal>
                                    </Box>
                                </Form>
                            )}
                        </Formik>
                    )}
                </Grid>
                <Grid item xs={0.5} />
            </Grid>

            <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 700}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
                    <Formik
                        initialValues={{ reasonForRejection: "" }}
                        validationSchema={rejectValidationSchema}
                        onSubmit={async (values) => {
                            try {
                                await queryGraphql(`mutation rejectSolutionNotebooks($input: RejectSolutionInput) { rejectSolutionNotebooks(input: $input) { id } }`, { input: { id: notebook.id, reasonForRejection: values.reasonForRejection } });
                                navigate("/aisolutions/requests/notebooks");
                            } catch (err) { console.error("Rejection failed:", err); }
                        }}
                    >
                        {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
                            <Form id="notebook-reject-form">
                                <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Notebook Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close /></IconButton></Box>
                                <Divider />
                                <Box p={3}>
                                    <CompulsoryFieldsLabel fieldName="Comments" />
                                    <TextField fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
                                    <ErrorMessage name="reasonForRejection">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
                                </Box>
                                <Divider />
                                <Box sx={{ p: 2, textAlign: "right" }}>
                                    <Button type="submit" form="notebook-reject-form" variant="contained" disabled={!isValid || !dirty} sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} >Reject</Button>
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </Modal>
        </Container>
    );
};


// --- Page Shell Component ---
const NotebooksApproveFormPage: React.FC = () => {
    const theme = useTheme();
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(true);
    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); };
    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer index={"requests-notebooks"} onStateChange={handleDrawerStateChange} />
            <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
                <NotebookApproveFormPageContent />
            </Box>
        </Box>
    );
};

export default NotebooksApproveFormPage;




// // Filename: NotebookApproveFormPage.tsx
// import React, { useState, useEffect } from 'react';
// import {
//     Box, Button, Container, Grid, TextField, Typography, MenuItem, IconButton, Divider,
//     Breadcrumbs, Link, Modal, capitalize, Avatar, Tooltip, Chip, useTheme
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import moment from "moment";
// import { useAuth } from "../../../helpers/AuthProvider"; // Adjust path if needed
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields"; // Adjust path if needed
// import queryGraphql from "../../../helpers/ApiInterface"; // Adjust path if needed
// import MiniBot from "../../MiniBot"; // Adjust path if needed
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed
// import { Close } from '@mui/icons-material';

// // --- Validation Schemas ---
// const approvalValidationSchema = Yup.object().shape({
//     jupyterLabVersion: Yup.string().required("This field is required"),
//     machineType: Yup.string().required("This field is required"),
//     network: Yup.string().required("This field is required").strict().lowercase(),
//     subNetwork: Yup.string().required("This field is required").strict().lowercase(),
// });

// const rejectValidationSchema = Yup.object().shape({
//     reasonForRejection: Yup.string()
//         .required("This field is required")
//         .matches(/^[a-zA-Z0-9-\s.,!?()']*$/, "Comments can only contain alphanumeric characters and basic punctuation.")
//         .strict(),
// });

// // --- Data for Dropdowns ---
// const jupyterLabVersionList = ["4.x", "3.x"];
// const machineTypeList = [
//     "e2-standard-2", "e2-standard-4", "e2-standard-8", "e2-standard-16",
//     "n2-standard-2", "n2-standard-4", "n2-standard-8", "n2-standard-16",
//     "n1-standard-2", "n1-standard-4", "n1-standard-8", "n1-standard-16",
// ];

// // --- Helper Component ---
// const DetailItem: React.FC<{ label: string; value?: string | number | null; }> = ({ label, value }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
//         </Box>
//     );
// };


// // --- Main Content Component ---
// const NotebookApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [notebook, setNotebook] = useState<any>(null);
//     const [openRejectModal, setOpenRejectModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);

//     React.useEffect(() => {
//         const fetchNotebookData = async (notebookId: string) => {
//             try {
//                 const data = await queryGraphql(
//                     `
//                     query GetNotebook($getNotebookId: String!) {
//                       getNotebook(id: $getNotebookId) {
//                         id
//                         name
//                         cloudProvider
//                         application {
//                            id
//                            applicationName
//                         }
//                         status
//                         createdAt
//                         updatedAt
//                         reasonForRejection
//                         solutionOwner {
//                           name
//                           googleProfilePicture
//                         }
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             subNetwork
//                             network
//                           }
//                         }
//                       }
//                     }
//                     `,
//                     { getNotebookId: notebookId }
//                 );
//                 if (data.getNotebook) {
//                     setNotebook(data.getNotebook);
//                 }
//             } catch (error) {
//                 console.log("Error fetching notebook details:", error);
//             }
//         };
//         if (id) fetchNotebookData(id);
//     }, [id]);
//     console.log("notebook data", notebook);
//     const getStatusInfo = (status: string | undefined) => {
//         if (!status) return { text: 'Unknown', color: '#FDB02210', icon: '/assets/clock.svg' };
//         const lowerStatus = status.toLowerCase();
//         if (["active", "approved", "success"].includes(lowerStatus)) return { text: 'Active', color: '#12B76A10', icon: '/assets/tickcircle.svg' };
//         if (["rejected", "failed"].includes(lowerStatus)) return { text: 'Rejected', color: '#F0443810', icon: '/assets/reject.svg' };
//         return { text: 'Pending', color: '#FDB02210', icon: '/assets/clock.svg' };
//     };
//     const statusInfo = getStatusInfo(notebook?.status);

//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/aisolutions/requests/notebooks">
//                         Requests
//                     </Link>
//                     <Typography color="text.primary">Request Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/notebooks")}>
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         Request Details
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={0.5} />
//                 <Grid item xs={11}>
//                     {notebook && (
//                         <Formik
//                             initialValues={{
//                                 jupyterLabVersion: '4.x',
//                                 machineType: 'e2-standard-4',
//                                 network: '',
//                                 subNetwork: '',
//                             }}
//                             validationSchema={approvalValidationSchema}
//                             onSubmit={async (values) => {
//                                 const approvalInput = {
//                                     id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                 };
//                                 try {
//                                     const approvalData = await queryGraphql(
//                                         `mutation ApproveSolutionNotebooks($input: ApproveSolutionInput) {
//                                             approveSolutionNotebooks(input: $input) { id }
//                                         }`,
//                                         { input: {
//                                             id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                         } }
//                                     );
//                                     if (approvalData?.approveSolutionNotebooks?.id ) {
//                                         navigate("/aisolutions/requests/notebooks");
//                                     }
//                                 } catch (err) {
//                                     console.error("Approval failed:", err);
//                                 }
//                             }}
//                         >
//                             {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                                 <Form id="notebook-approve-form">
//                                     <Box mx={4}>
//                                         <Typography fontSize="16px" my={1}>
//                                             Review the notebook request and fill the environment & networking details to approve.
//                                         </Typography>
//                                         <Box>
//                                             <Box bgcolor={statusInfo.color} justifyContent="space-between" alignItems="center" display="flex" borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
//                                                 <Box>
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-Bold">{notebook.name}</Typography>
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="12px">{`Requested By: ${notebook.solutionOwner?.name} | On: ${moment(Number(notebook.createdAt)).format("DD/MM/YYYY")}`}</Typography>
//                                                 </Box>
//                                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                                     <img src={statusInfo.icon} alt="Status" style={{ width: "20px", marginRight: "8px" }} />
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">{statusInfo.text}</Typography>
//                                                 </Box>
//                                             </Box>
//                                             <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                                                 <Grid container spacing={1}>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Cloud Provider" value={notebook.cloudProvider} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Application" value={notebook.application?.applicationName} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Workbench Type" value={notebook.details.notebooks.workbenchType} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Region" value={notebook.details.notebooks.region} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Zone" value={notebook.details.notebooks.zone} /></Grid>
//                                                 </Grid>
//                                             </Box>

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                     <Typography fontSize="14px" fontFamily="Gilroy-Bold">Environment & Networking Details</Typography>
//                                                     <Divider sx={{ marginY: 2 }} />
//                                                     <Grid container spacing={2}>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="JupyterLab version" />
//                                                             <TextField select fullWidth name="jupyterLabVersion" value={values.jupyterLabVersion} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.jupyterLabVersion && Boolean(errors.jupyterLabVersion)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {jupyterLabVersionList.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="jupyterLabVersion">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Machine type" />
//                                                             <TextField select fullWidth name="machineType" value={values.machineType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.machineType && Boolean(errors.machineType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {machineTypeList.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="machineType">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Network" />
//                                                             <TextField fullWidth name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/global/networks/vpc-name" error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="network">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Sub-network" />
//                                                             <TextField fullWidth name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/regions/us-central1/subnetworks/subnet-name" error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="subNetwork">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                     </Grid>
//                                                 </Box>
//                                             )}
                                            
//                                             {notebook.status !== "pending" && (
//                                                 <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
//                                                     {notebook.status === "active" && (
//                                                         <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
                                                                            
//                                                                             <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                                                                                 Environment and Networking
//                                                                             </Typography>
//                                                                             <Grid container spacing={3}>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.jupyterLabVersion}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.machineType}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.network}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.subNetwork}</Typography>
//                                                                                 </Grid>
//                                                                             </Grid>
//                                                                         </Box>
//                                                     )}
//                                                     {notebook.status === "rejected" && <DetailItem label="Reason for Rejection" value={notebook.reasonForRejection} />}
                                                    
//                                                 </Box>
//                                             )}

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
//                                                     <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
//                                                     <Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large"  disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
//                                                 </Box>
//                                             )}
//                                         </Box>

//                                         <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
//                                             <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 600}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Notebook</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close /></IconButton></Box>
//                                                 <Divider />
//                                                 <Typography p={3}>Are you sure you want to approve this Notebook Request?</Typography>
//                                                 <Divider />
//                                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                                     <Button type="submit" form="notebook-approve-form" sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large">Approve</Button>
//                                                 </Box>
//                                             </Box>
//                                         </Modal>
//                                     </Box>
//                                 </Form>
//                             )}
//                         </Formik>
//                     )}
//                 </Grid>
//                 <Grid item xs={0.5} />
//             </Grid>

//             <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
//                 <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 700}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                     <Formik
//                         initialValues={{ reasonForRejection: "" }}
//                         validationSchema={rejectValidationSchema}
//                         onSubmit={async (values) => {
//                             try {
//                                 await queryGraphql(`mutation rejectSolutionNotebooks($input: RejectSolutionInput) { rejectSolutionNotebooks(input: $input) { id } }`, { input: { id: notebook.id, reasonForRejection: values.reasonForRejection } });
//                                 navigate("/aisolutions/requests/notebooks");
//                             } catch (err) { console.error("Rejection failed:", err); }
//                         }}
//                     >
//                         {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                             <Form id="notebook-reject-form">
//                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Notebook Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close /></IconButton></Box>
//                                 <Divider />
//                                 <Box p={3}>
//                                     <CompulsoryFieldsLabel fieldName="Comments" />
//                                     <TextField fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
//                                     <ErrorMessage name="reasonForRejection">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                     <Button type="submit" form="notebook-reject-form" variant="contained" disabled={!isValid || !dirty} sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} >Reject</Button>
//                                 </Box>
//                             </Form>
//                         )}
//                     </Formik>
//                 </Box>
//             </Modal>
//         </Container>
//     );
// };


// // --- Page Shell Component ---
// const NotebooksApproveFormPage: React.FC = () => {
//     const theme = useTheme();
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(true);
//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); };
//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"requests-notebooks"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
//                 <NotebookApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksApproveFormPage;



// // Filename: NotebookApproveFormPage.tsx
// import React, { useState, useEffect } from 'react';
// import {
//     Box, Button, Container, Grid, TextField, Typography, MenuItem, IconButton, Divider,
//     Breadcrumbs, Link, Modal, capitalize, Avatar, Tooltip, Chip, useTheme
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import moment from "moment";
// import { useAuth } from "../../../helpers/AuthProvider"; // Adjust path if needed
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields"; // Adjust path if needed
// import queryGraphql from "../../../helpers/ApiInterface"; // Adjust path if needed
// import MiniBot from "../../MiniBot"; // Adjust path if needed
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed
// import { Close } from '@mui/icons-material';

// // --- Validation Schemas ---
// const approvalValidationSchema = Yup.object().shape({
//     jupyterLabVersion: Yup.string().required("This field is required"),
//     machineType: Yup.string().required("This field is required"),
//     network: Yup.string().required("This field is required").strict().lowercase(),
//     subNetwork: Yup.string().required("This field is required").strict().lowercase(),
// });

// const rejectValidationSchema = Yup.object().shape({
//     reasonForRejection: Yup.string()
//         .required("This field is required")
//         .matches(/^[a-zA-Z0-9-\s.,!?()']*$/, "Comments can only contain alphanumeric characters and basic punctuation.")
//         .strict(),
// });

// // --- Data for Dropdowns ---
// const jupyterLabVersionList = ["4.x", "3.x"];
// const machineTypeList = [
//     "e2-standard-2", "e2-standard-4", "e2-standard-8", "e2-standard-16",
//     "n2-standard-2", "n2-standard-4", "n2-standard-8", "n2-standard-16",
//     "n1-standard-2", "n1-standard-4", "n1-standard-8", "n1-standard-16",
// ];

// // --- Helper Component ---
// const DetailItem: React.FC<{ label: string; value?: string | number | null; }> = ({ label, value }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
//         </Box>
//     );
// };


// // --- Main Content Component ---
// const NotebookApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [notebook, setNotebook] = useState<any>(null);
//     const [openRejectModal, setOpenRejectModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);

//     React.useEffect(() => {
//         const fetchNotebookData = async (notebookId: string) => {
//             try {
//                 const data = await queryGraphql(
//                     `
//                     query GetNotebook($getNotebookId: String!) {
//                       getNotebook(id: $getNotebookId) {
//                         id
//                         name
//                         application {
//                            id
//                            applicationName
//                         }
//                         status
//                         createdAt
//                         updatedAt
//                         reasonForRejection
//                         solutionOwner {
//                           name
//                           googleProfilePicture
//                         }
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             subNetwork
//                             network
//                           }
//                         }
//                       }
//                     }
//                     `,
//                     { getNotebookId: notebookId }
//                 );
//                 if (data.getNotebook) {
//                     setNotebook(data.getNotebook);
//                 }
//             } catch (error) {
//                 console.log("Error fetching notebook details:", error);
//             }
//         };
//         if (id) fetchNotebookData(id);
//     }, [id]);
//     console.log("notebook data", notebook);
//     const getStatusInfo = (status: string | undefined) => {
//         if (!status) return { text: 'Unknown', color: '#FDB02210', icon: '/assets/clock.svg' };
//         const lowerStatus = status.toLowerCase();
//         if (["active", "approved", "success"].includes(lowerStatus)) return { text: 'Active', color: '#12B76A10', icon: '/assets/tickcircle.svg' };
//         if (["rejected", "failed"].includes(lowerStatus)) return { text: 'Rejected', color: '#F0443810', icon: '/assets/reject.svg' };
//         return { text: 'Pending', color: '#FDB02210', icon: '/assets/clock.svg' };
//     };
//     const statusInfo = getStatusInfo(notebook?.status);

//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/aisolutions/requests/notebooks">
//                         Requests
//                     </Link>
//                     <Typography color="text.primary">Request Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/notebooks")}>
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         Request Details
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={0.5} />
//                 <Grid item xs={11}>
//                     {notebook && (
//                         <Formik
//                             initialValues={{
//                                 jupyterLabVersion: '4.x',
//                                 machineType: 'e2-standard-4',
//                                 network: '',
//                                 subNetwork: '',
//                             }}
//                             validationSchema={approvalValidationSchema}
//                             onSubmit={async (values) => {
//                                 const approvalInput = {
//                                     id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                 };
//                                 try {
//                                     const approvalData = await queryGraphql(
//                                         `mutation ApproveSolutionNotebooks($input: ApproveSolutionInput) {
//                                             approveSolutionNotebooks(input: $input) { id }
//                                         }`,
//                                         { input: {
//                                             id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                         } }
//                                     );
//                                     if (approvalData?.approveSolutionNotebooks?.id ) {
//                                         navigate("/aisolutions/requests/notebooks");
//                                     }
//                                 } catch (err) {
//                                     console.error("Approval failed:", err);
//                                 }
//                             }}
//                         >
//                             {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                                 <Form id="notebook-approve-form">
//                                     <Box mx={4}>
//                                         <Typography fontSize="16px" my={1}>
//                                             Review the notebook request and fill the environment & networking details to approve.
//                                         </Typography>
//                                         <Box>
//                                             <Box bgcolor={statusInfo.color} justifyContent="space-between" alignItems="center" display="flex" borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
//                                                 <Box>
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-Bold">{notebook.name}</Typography>
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="12px">{`Requested By: ${notebook.solutionOwner?.name} | On: ${moment(Number(notebook.createdAt)).format("DD/MM/YYYY")}`}</Typography>
//                                                 </Box>
//                                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                                     <img src={statusInfo.icon} alt="Status" style={{ width: "20px", marginRight: "8px" }} />
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">{statusInfo.text}</Typography>
//                                                 </Box>
//                                             </Box>
//                                             <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                                                 <Grid container spacing={1}>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Application" value={notebook.application?.applicationName} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Workbench Type" value={notebook.details.notebooks.workbenchType} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Region" value={notebook.details.notebooks.region} /></Grid>
//                                                     <Grid item xs={12} sm={3}><DetailItem label="Zone" value={notebook.details.notebooks.zone} /></Grid>
//                                                 </Grid>
//                                             </Box>

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                     <Typography fontSize="14px" fontFamily="Gilroy-Bold">Environment & Networking Details</Typography>
//                                                     <Divider sx={{ marginY: 2 }} />
//                                                     <Grid container spacing={2}>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="JupyterLab version" />
//                                                             <TextField select fullWidth name="jupyterLabVersion" value={values.jupyterLabVersion} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.jupyterLabVersion && Boolean(errors.jupyterLabVersion)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {jupyterLabVersionList.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="jupyterLabVersion">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Machine type" />
//                                                             <TextField select fullWidth name="machineType" value={values.machineType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.machineType && Boolean(errors.machineType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {machineTypeList.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="machineType">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Network" />
//                                                             <TextField fullWidth name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/global/networks/vpc-name" error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="network">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Sub-network" />
//                                                             <TextField fullWidth name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/regions/us-central1/subnetworks/subnet-name" error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="subNetwork">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                     </Grid>
//                                                 </Box>
//                                             )}
                                            
//                                             {notebook.status !== "pending" && (
//                                                 <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
//                                                     {notebook.status === "active" && (
//                                                         <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
                                                                            
//                                                                             <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                                                                                 Environment and Networking
//                                                                             </Typography>
//                                                                             <Grid container spacing={3}>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.jupyterLabVersion}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.machineType}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.network}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.subNetwork}</Typography>
//                                                                                 </Grid>
//                                                                             </Grid>
//                                                                         </Box>
//                                                     )}
//                                                     {notebook.status === "rejected" && <DetailItem label="Reason for Rejection" value={notebook.reasonForRejection} />}
                                                    
//                                                 </Box>
//                                             )}

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
//                                                     <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
//                                                     <Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large"  disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
//                                                 </Box>
//                                             )}
//                                         </Box>

//                                         <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
//                                             <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 600}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Notebook</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close /></IconButton></Box>
//                                                 <Divider />
//                                                 <Typography p={3}>Are you sure you want to approve this Notebook Request?</Typography>
//                                                 <Divider />
//                                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                                     <Button type="submit" form="notebook-approve-form" sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large">Approve</Button>
//                                                 </Box>
//                                             </Box>
//                                         </Modal>
//                                     </Box>
//                                 </Form>
//                             )}
//                         </Formik>
//                     )}
//                 </Grid>
//                 <Grid item xs={0.5} />
//             </Grid>

//             <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
//                 <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 700}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                     <Formik
//                         initialValues={{ reasonForRejection: "" }}
//                         validationSchema={rejectValidationSchema}
//                         onSubmit={async (values) => {
//                             try {
//                                 await queryGraphql(`mutation rejectSolutionNotebooks($input: RejectSolutionInput) { rejectSolutionNotebooks(input: $input) { id } }`, { input: { id: notebook.id, reasonForRejection: values.reasonForRejection } });
//                                 navigate("/aisolutions/requests/notebooks");
//                             } catch (err) { console.error("Rejection failed:", err); }
//                         }}
//                     >
//                         {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                             <Form id="notebook-reject-form">
//                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Notebook Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close /></IconButton></Box>
//                                 <Divider />
//                                 <Box p={3}>
//                                     <CompulsoryFieldsLabel fieldName="Comments" />
//                                     <TextField fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
//                                     <ErrorMessage name="reasonForRejection">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                     <Button type="submit" form="notebook-reject-form" variant="contained" disabled={!isValid || !dirty} sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} >Reject</Button>
//                                 </Box>
//                             </Form>
//                         )}
//                     </Formik>
//                 </Box>
//             </Modal>
//         </Container>
//     );
// };


// // --- Page Shell Component ---
// const NotebooksApproveFormPage: React.FC = () => {
//     const theme = useTheme();
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(true);
//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); };
//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"requests-notebooks"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
//                 <NotebookApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default NotebooksApproveFormPage;



// // Filename: NotebookApproveFormPage.tsx
// import React, { useState, useEffect } from 'react';
// import {
//     Box, Button, Container, Grid, TextField, Typography, MenuItem, IconButton, Divider,
//     Breadcrumbs, Link, Modal, capitalize, Avatar, Tooltip, Chip, useTheme
// } from "@mui/material";
// import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
// import { useNavigate, useParams } from "react-router-dom";
// import { Formik, Form, ErrorMessage } from "formik";
// import * as Yup from "yup";
// import moment from "moment";
// import { useAuth } from "../../../helpers/AuthProvider"; // Adjust path if needed
// import CompulsoryFieldsLabel from "../../../helpers/CompulsoryFields"; // Adjust path if needed
// import queryGraphql from "../../../helpers/ApiInterface"; // Adjust path if needed
// import MiniBot from "../../MiniBot"; // Adjust path if needed
// import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer"; // Adjust path if needed
// import { Close } from '@mui/icons-material';

// // --- Validation Schemas ---
// const approvalValidationSchema = Yup.object().shape({
//     jupyterLabVersion: Yup.string().required("This field is required"),
//     machineType: Yup.string().required("This field is required"),
//     network: Yup.string().required("This field is required").strict().lowercase(),
//     subNetwork: Yup.string().required("This field is required").strict().lowercase(),
// });

// const rejectValidationSchema = Yup.object().shape({
//     reasonForRejection: Yup.string()
//         .required("This field is required")
//         .matches(/^[a-zA-Z0-9-\s.,!?()']*$/, "Comments can only contain alphanumeric characters and basic punctuation.")
//         .strict(),
// });

// // --- Data for Dropdowns ---
// const jupyterLabVersionList = ["4.x", "3.x"];
// const machineTypeList = [
//     "e2-standard-2", "e2-standard-4", "e2-standard-8", "e2-standard-16",
//     "n2-standard-2", "n2-standard-4", "n2-standard-8", "n2-standard-16",
//     "n1-standard-2", "n1-standard-4", "n1-standard-8", "n1-standard-16",
// ];

// // --- Helper Component ---
// const DetailItem: React.FC<{ label: string; value?: string | number | null; }> = ({ label, value }) => {
//     const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
//     return (
//         <Box>
//             <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
//             <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{displayValue}</Typography>
//         </Box>
//     );
// };


// // --- Main Content Component ---
// const NotebookApproveFormPageContent: React.FC = () => {
//     const navigate = useNavigate();
//     const { id } = useParams<{ id: string }>();
//     const { user } = useAuth();
//     const [notebook, setNotebook] = useState<any>(null);
//     const [openRejectModal, setOpenRejectModal] = useState(false);
//     const [openApproveModal, setOpenApproveModal] = useState(false);

//     React.useEffect(() => {
//         const fetchNotebookData = async (notebookId: string) => {
//             try {
//                 const data = await queryGraphql(
//                     `
//                     query GetNotebook($getNotebookId: String!) {
//                       getNotebook(id: $getNotebookId) {
//                         id
//                         name
//                         status
//                         createdAt
//                         updatedAt
//                         reasonForRejection
//                         solutionOwner {
//                           name
//                           googleProfilePicture
//                         }
//                         costLabelPairs {
//                           cost
//                           label
//                         }
//                         details {
//                           notebooks {
//                             region
//                             zone
//                             workbenchType
//                             jupyterLabVersion
//                             machineType
//                             subNetwork
//                             network
//                           }
//                         }
//                       }
//                     }
//                     `,
//                     { getNotebookId: notebookId }
//                 );
//                 if (data.getNotebook) {
//                     setNotebook(data.getNotebook);
//                 }
//             } catch (error) {
//                 console.log("Error fetching notebook details:", error);
//             }
//         };
//         if (id) fetchNotebookData(id);
//     }, [id]);
//     console.log("notebook data", notebook);
//     const getStatusInfo = (status: string | undefined) => {
//         if (!status) return { text: 'Unknown', color: '#FDB02210', icon: '/assets/clock.svg' };
//         const lowerStatus = status.toLowerCase();
//         if (["active", "approved", "success"].includes(lowerStatus)) return { text: 'Active', color: '#12B76A10', icon: '/assets/tickcircle.svg' };
//         if (["rejected", "failed"].includes(lowerStatus)) return { text: 'Rejected', color: '#F0443810', icon: '/assets/reject.svg' };
//         return { text: 'Pending', color: '#FDB02210', icon: '/assets/clock.svg' };
//     };
//     const statusInfo = getStatusInfo(notebook?.status);

//     return (
//         <Container disableGutters maxWidth={false} sx={{ bgcolor: "#fafafa", minHeight: "100vh" }}>
//             <MiniBot />
//             <Box sx={{ p: 2, bgcolor: "#FFFFFF" }}>
//                 <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: "0.875rem", color: "gray" }}>
//                     <Link underline="hover" color="inherit" href="/aisolutions/requests/notebooks">
//                         Requests
//                     </Link>
//                     <Typography color="text.primary">Request Details</Typography>
//                 </Breadcrumbs>
//                 <Box display="flex" alignItems="center" mt={1}>
//                     <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate("/aisolutions/requests/notebooks")}>
//                         <ArrowBackIosNewIcon fontSize="small" />
//                     </IconButton>
//                     <Typography fontFamily="Gilroy-Bold" fontSize="18px">
//                         Request Details
//                     </Typography>
//                 </Box>
//             </Box>
//             <Grid container>
//                 <Grid item xs={0.5} />
//                 <Grid item xs={11}>
//                     {notebook && (
//                         <Formik
//                             initialValues={{
//                                 jupyterLabVersion: '4.x',
//                                 machineType: 'e2-standard-4',
//                                 network: '',
//                                 subNetwork: '',
//                             }}
//                             validationSchema={approvalValidationSchema}
//                             onSubmit={async (values) => {
//                                 const approvalInput = {
//                                     id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                 };
//                                 try {
//                                     const approvalData = await queryGraphql(
//                                         `mutation ApproveSolutionNotebooks($input: ApproveSolutionInput) {
//                                             approveSolutionNotebooks(input: $input) { id }
//                                         }`,
//                                         { input: {
//                                             id: notebook.id,
//                                     details:{
//                                         notebooks:{
//                                             jupyterLabVersion: values.jupyterLabVersion,
//                                             machineType : values.machineType,
//                                             subNetwork : values.subNetwork,
//                                             network : values.network
//                                         }
//                                     },
//                                         } }
//                                     );
//                                     // const statusData = await queryGraphql(
//                                     //     `mutation UpdateResourceStatus($input: ResourceStatusUpdateInput!) {
//                                     //         updateResourceStatus(input: $input) { id currentStatus}
//                                     //     }`,
//                                     //     { input: { resourceId: notebook.id, currentStatus: "approved" } }
//                                     // );
//                                     //console.log("ddata----->",approvalData?.approveSolutionNotebooks?.id)
//                                     if (approvalData?.approveSolutionNotebooks?.id ) {
//                                         navigate("/aisolutions/requests/notebooks");
//                                     }
//                                     //console.log("approval data", approvalData);
//                                     //console.log("resource status", statusData?.updateResourceStatus?.currentStatus)
//                                 } catch (err) {
//                                     console.error("Approval failed:", err);
//                                 }
//                             }}
//                         >
//                             {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                                 <Form id="notebook-approve-form">
//                                     <Box mx={4}>
//                                         <Typography fontSize="16px" my={1}>
//                                             Review the notebook request and fill the environment & networking details to approve.
//                                         </Typography>
//                                         <Box>
//                                             <Box bgcolor={statusInfo.color} justifyContent="space-between" alignItems="center" display="flex" borderRadius="12px 12px 0px 0px" p={2} boxShadow={1}>
//                                                 <Box>
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-Bold">{notebook.name}</Typography>
//                                                     <Typography fontFamily="Gilroy-Regular" fontSize="12px">{`Requested By: ${notebook.solutionOwner?.name} | On: ${moment(Number(notebook.createdAt)).format("DD/MM/YYYY")}`}</Typography>
//                                                 </Box>
//                                                 <Box sx={{ display: "flex", alignItems: "center" }}>
//                                                     <img src={statusInfo.icon} alt="Status" style={{ width: "20px", marginRight: "8px" }} />
//                                                     <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">{statusInfo.text}</Typography>
//                                                 </Box>
//                                             </Box>
//                                             <Box bgcolor="#fff" p={3} borderRadius="0px 0px 12px 12px" boxShadow={1}>
//                                                 <Grid container spacing={1}>
//                                                     <Grid item xs={12} sm={4}><DetailItem label="Workbench Type" value={notebook.details.notebooks.workbenchType} /></Grid>
//                                                     <Grid item xs={12} sm={4}><DetailItem label="Region" value={notebook.details.notebooks.region} /></Grid>
//                                                     <Grid item xs={12} sm={4}><DetailItem label="Zone" value={notebook.details.notebooks.zone} /></Grid>
//                                                 </Grid>
//                                                 {/* <Box><Typography gutterBottom fontFamily="Gilroy-Regular" fontSize="12px" mt={2}>Cost Labels</Typography><Box ml={-0.5} display="flex" flexWrap="wrap" gap={1}>{notebook.costLabelPairs?.map((e: any, idx: number) => (<Chip key={idx} size="small" sx={{ bgcolor: "#10101010", border: "1px solid black" }} variant="filled" label={`${e.cost} • ${e.label}`} />))}</Box></Box> */}
//                                             </Box>

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow={1}>
//                                                     <Typography fontSize="14px" fontFamily="Gilroy-Bold">Environment & Networking Details</Typography>
//                                                     <Divider sx={{ marginY: 2 }} />
//                                                     <Grid container spacing={2}>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="JupyterLab version" />
//                                                             <TextField select fullWidth name="jupyterLabVersion" value={values.jupyterLabVersion} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.jupyterLabVersion && Boolean(errors.jupyterLabVersion)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {jupyterLabVersionList.map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="jupyterLabVersion">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Machine type" />
//                                                             <TextField select fullWidth name="machineType" value={values.machineType} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel error={touched.machineType && Boolean(errors.machineType)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }}>
//                                                                 {machineTypeList.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
//                                                             </TextField>
//                                                             <ErrorMessage name="machineType">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Network" />
//                                                             <TextField fullWidth name="network" value={values.network} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/global/networks/vpc-name" error={touched.network && Boolean(errors.network)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="network">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                         <Grid item xs={12} sm={6}>
//                                                             <CompulsoryFieldsLabel fieldName="Sub-network" />
//                                                             <TextField fullWidth name="subNetwork" value={values.subNetwork} onChange={handleChange} onBlur={handleBlur} variant="filled" hiddenLabel placeholder="e.g., projects/vpc-project/regions/us-central1/subnetworks/subnet-name" error={touched.subNetwork && Boolean(errors.subNetwork)} InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} />
//                                                             <ErrorMessage name="subNetwork">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                                         </Grid>
//                                                     </Grid>
//                                                 </Box>
//                                             )}
                                            
//                                             {notebook.status !== "pending" && (
//                                                 <Box bgcolor="#fff" p={2} borderRadius="12px" boxShadow={1} mt={2}>
//                                                     {notebook.status === "active" && (
//                                                         <Box sx={{ mt: 0, p: 2.5, py: 1 }}>
                                                                            
//                                                                             <Typography component="h3" sx={{fontFamily:'Gilroy-Medium', fontSize:'14px', color:'#333333', pb:"1vh" }}>
//                                                                                 Environment and Networking
//                                                                             </Typography>
//                                                                             <Grid container spacing={3}>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>JupyterLab Version</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.jupyterLabVersion}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Machine Type</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.machineType}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.network}</Typography>
//                                                                                 </Grid>
//                                                                                 <Grid item xs={6} sm={3}>
//                                                                                     <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Sub-network</Typography>
//                                                                                     <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{notebook?.details?.notebooks?.subNetwork}</Typography>
//                                                                                 </Grid>
//                                                                             </Grid>
//                                                                         </Box>
//                                                     )}
//                                                     {notebook.status === "rejected" && <DetailItem label="Reason for Rejection" value={notebook.reasonForRejection} />}
                                                    
//                                                 </Box>
//                                             )}

//                                             {notebook.status === "pending" && user.roles.some((element: string) => ["infrastructure_admin"].includes(element)) && (
//                                                 <Box display="flex" justifyContent="flex-end" gap={2} mt={4} pb={4}>
//                                                     <Button sx={{color: "black",borderColor: "black",fontWeight: "bold",textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} variant="outlined" size="large" onClick={() => setOpenRejectModal(true)}>Reject Request</Button>
//                                                     <Button sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large"  disabled={!isValid || !dirty} onClick={() => setOpenApproveModal(true)}>Approve Request</Button>
//                                                 </Box>
//                                             )}
//                                         </Box>

//                                         <Modal open={openApproveModal} onClose={() => setOpenApproveModal(false)}>
//                                             <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 600}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Approve Notebook</Typography><IconButton onClick={() => setOpenApproveModal(false)}><Close /></IconButton></Box>
//                                                 <Divider />
//                                                 <Typography p={3}>Are you sure you want to approve this Notebook Request?</Typography>
//                                                 <Divider />
//                                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                                     <Button type="submit" form="notebook-approve-form" sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} 
//                                                             variant="contained" 
//                                                             size="large">Approve</Button>
//                                                 </Box>
//                                             </Box>
//                                         </Modal>
//                                     </Box>
//                                 </Form>
//                             )}
//                         </Formik>
//                     )}
//                 </Grid>
//                 <Grid item xs={0.5} />
//             </Grid>

//             <Modal open={openRejectModal} onClose={() => setOpenRejectModal(false)}>
//                 <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: {xs: '90%', md: 700}, bgcolor: "white", borderRadius: "16px", boxShadow: 24 }}>
//                     <Formik
//                         initialValues={{ reasonForRejection: "" }}
//                         validationSchema={rejectValidationSchema}
//                         onSubmit={async (values) => {
//                             try {
//                                 await queryGraphql(`mutation rejectSolutionNotebooks($input: RejectSolutionInput) { rejectSolutionNotebooks(input: $input) { id } }`, { input: { id: notebook.id, reasonForRejection: values.reasonForRejection } });
//                                 //await queryGraphql(`mutation UpdateResourceStatus($input: ResourceStatusUpdateInput!) { updateResourceStatus(input: $input) { id } }`, { input: { resourceId: notebook.id, currentStatus: "rejected" } });
//                                 navigate("/aisolutions/requests/notebooks");
//                             } catch (err) { console.error("Rejection failed:", err); }
//                         }}
//                     >
//                         {({ handleChange, handleBlur, values, errors, touched, isValid, dirty }) => (
//                             <Form id="notebook-reject-form">
//                                 <Box display="flex" justifyContent="space-between" p={2}><Typography fontFamily="Gilroy-SemiBold" fontSize="20px">Reject Notebook Request</Typography><IconButton onClick={() => setOpenRejectModal(false)}><Close /></IconButton></Box>
//                                 <Divider />
//                                 <Box p={3}>
//                                     <CompulsoryFieldsLabel fieldName="Comments" />
//                                     <TextField fullWidth multiline rows={4} name="reasonForRejection" value={values.reasonForRejection} onChange={handleChange} onBlur={handleBlur} placeholder="Enter comments for rejection..." hiddenLabel variant="filled" InputProps={{ disableUnderline: true, style: { borderRadius: "8px" } }} error={touched.reasonForRejection && Boolean(errors.reasonForRejection)} />
//                                     <ErrorMessage name="reasonForRejection">{msg => <Typography color="error" variant="caption">{msg}</Typography>}</ErrorMessage>
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ p: 2, textAlign: "right" }}>
//                                     <Button type="submit" form="notebook-reject-form" variant="contained" disabled={!isValid || !dirty} sx={{backgroundColor: "black",color: "white",fontWeight: "bold","&:hover": {backgroundColor: "white",color: "black",borderColor: "black",},textTransform: "none",px: 4,fontFamily: "Gilroy-Bold",fontSize: "14px"}} >Reject</Button>
//                                 </Box>
//                             </Form>
//                         )}
//                     </Formik>
//                 </Box>
//             </Modal>
//         </Container>
//     );
// };


// // --- Page Shell Component ---
// const NotebookApproveFormPage: React.FC = () => {
//     const theme = useTheme();
//     const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(true);
//     const handleDrawerStateChange = (isOpenFromDrawer: boolean) => { setIsDrawerActuallyOpen(isOpenFromDrawer); };
//     const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

//     return (
//         <Box sx={{ display: 'flex', minHeight: '100vh' }}>
//             <MainDrawer index={"requests-notebooks"} onStateChange={handleDrawerStateChange} />
//             <Box component="main" sx={{ flexGrow: 1, p: 0, transition: theme.transitions.create('margin', { easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.enteringScreen, }), marginLeft: { xs: 0, sm: `${currentMarginLeft}px` } }}>
//                 <NotebookApproveFormPageContent />
//             </Box>
//         </Box>
//     );
// };

// export default NotebookApproveFormPage;